import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import { useAppSelector } from '@verantos-dev/app-state-base';
import { selectFullCohortDsl } from '@verantos-dev/app-state-cohort-builder';
import { useGenerateSQLTranslationMutation } from '@verantos-dev/data-access-cohorts';
import { SelectStatementType } from '@verantos-dev/types-cohorts';

import { type CohortTranslationProps } from './CohortTranslation';
import { useCohortDefinitionSqlContent } from './useCohortDefinitionSqlContent';

export const useCohortDefinitionSQLSection = (): CohortTranslationProps => {
  const intl = useIntl();
  const fullCohortDSL = useAppSelector(selectFullCohortDsl);
  const { projectId, cohortId } = useParams();
  const [messages, setMessages] = useState({
    main: intl.formatMessage({
      id: 'projects.cohortBuilderPage.cohortDefinitionSQLSection.noSQLMainMsg',
    }),
    sub: intl.formatMessage({
      id: 'projects.cohortBuilderPage.cohortDefinitionSQLSection.noSQLSubMsg',
    }),
  });

  const [
    generateSQLTranslation,
    {
      data: sqlTranslationData,
      error: sqlTranslationError,
      isLoading: isSQLTranslationLoading,
      isUninitialized: isSQLTranslationUninitialized,
    },
  ] = useGenerateSQLTranslationMutation();

  const { sqlContent } = useCohortDefinitionSqlContent({
    sqlQuery: sqlTranslationData?.data?.sqlQuery || '',
  });

  // Regenerate the SQL translation when the cohort DSL changes
  useEffect(() => {
    if (fullCohortDSL) {
      try {
        if (projectId && cohortId) {
          generateSQLTranslation({
            cohortDsl: fullCohortDSL,
            cohortId,
            projectId,
            selectStmtType: SelectStatementType.ALL,
          });
        }
      } catch (error) {
        console.error('Error generating SQL translation:', error);
      }
    }
  }, [cohortId, fullCohortDSL, generateSQLTranslation, projectId]);

  // Handle the SQL translation response
  useEffect(() => {
    if (isSQLTranslationUninitialized) {
      setMessages({
        main: intl.formatMessage({
          id: 'projects.cohortBuilderPage.cohortDefinitionSQLSection.noSQLMainMsg',
        }),
        sub: intl.formatMessage({
          id: 'projects.cohortBuilderPage.cohortDefinitionSQLSection.noSQLSubMsg',
        }),
      });
    } else if (isSQLTranslationLoading) {
      setMessages({
        main: intl.formatMessage({
          id: 'projects.cohortBuilderPage.cohortDefinitionSQLSection.translating',
        }),
        sub: intl.formatMessage({
          id: 'projects.cohortBuilderPage.cohortDefinitionSQLSection.translatingSubMessage',
        }),
      });
    } else if (sqlTranslationError) {
      setMessages({
        main: intl.formatMessage({
          id: 'projects.cohortBuilderPage.cohortDefinitionSQLSection.error',
        }),
        sub: intl.formatMessage({
          id: 'projects.cohortBuilderPage.cohortDefinitionSQLSection.errorSubMessage',
        }),
      });
    }
  }, [
    sqlTranslationData,
    sqlTranslationError,
    intl,
    isSQLTranslationUninitialized,
    isSQLTranslationLoading,
  ]);

  return {
    title: intl.formatMessage({
      id: 'projects.cohortBuilderPage.cohortDefinitionSQLSection.title',
    }),
    emptyStateMainMessage: messages.main,
    emptyStateSubMessage: messages.sub,
    content: sqlContent,
  };
};
